import { Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';

/**
 * Pipe que convierte fechas en formato array de Java (LocalDateTime serializado por Jackson)
 * al formato esperado por Angular's DatePipe.
 *
 * Jackson serializa LocalDateTime como: [año, mes(1-12), día, hora, minuto, segundo, nanosegundos]
 * Ejemplo: [2026, 5, 13, 9, 46, 59, 307550000]
 *
 * También acepta strings ISO y objetos Date válidos para mantener compatibilidad total.
 *
 * Uso en template:
 *   {{ item.createdAt | javaDate:'dd/MM/yyyy HH:mm' }}
 */
@Pipe({ name: 'javaDate' })
export class JavaDatePipe implements PipeTransform {

  private readonly datePipe = new DatePipe('es-CL');

  transform(value: any, format: string = 'dd/MM/yyyy HH:mm', timezone?: string, locale?: string): string | null {
    if (value == null) return null;

    const date = this.toDate(value);
    if (!date || isNaN(date.getTime())) return null;

    return this.datePipe.transform(date, format, timezone, locale);
  }

  private toDate(value: any): Date | null {
    // Caso 1: Ya es un Date
    if (value instanceof Date) return value;

    // Caso 2: Array de Java LocalDateTime [año, mes(1-12), día, h, m, s, nano]
    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = value;
      // JavaScript Date usa mes 0-indexado; Java usa 1-indexado
      return new Date(year, month - 1, day, hour, minute, second, Math.floor(nano / 1_000_000));
    }

    // Caso 3: String con comas (array serializado como string: "2026,5,13,9,46,59,307550000")
    if (typeof value === 'string' && /^\d+(,\d+)+$/.test(value.trim())) {
      const parts = value.split(',').map(Number);
      return this.toDate(parts);
    }

    // Caso 4: String ISO u otro formato que Date pueda parsear
    if (typeof value === 'string') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }

    // Caso 5: Número (timestamp Unix en ms)
    if (typeof value === 'number') {
      return new Date(value);
    }

    return null;
  }
}
